import React, { useState, useEffect, useContext } from 'react';
import {
  Box, TextField, Button, Typography, Container, Grid,
  FormControlLabel, Switch, Paper, Stack, FormLabel, MenuItem, Avatar, IconButton,
  Stepper, Step, StepLabel, CircularProgress, Modal, Backdrop, Fade, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, useTheme as useMuiTheme
} from '@mui/material';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, Cancel, Person as PersonIcon, AddCircleOutline, RemoveCircleOutline, ArrowForward, ArrowBack, Code, Palette, TextFields, Image, CheckCircle, Phone, Email, WhatsApp, Star } from '@mui/icons-material';
import { 
  fetchCardDetails, 
  updateCard, 
  createCard, 
  uploadCardImage, 
  uploadGalleryImage, 
  uploadAchievementImage,
  uploadAchievementPdf,
  uploadAchievementVideo 
} from '../../api/cardApi';
import { generateSlug, getFullUrl, getFrontendUrl, generatePublicUrl } from '../../utils/urlUtils';
import { usePage } from '../../context/PageContext';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { useTheme } from '../../context/ThemeContext';
import { drawerWidth } from '../Layout/SideMenu';
import PublicCard from './PublicCard'; // For live preview
import { QRCodeSVG } from 'qrcode.react';
import ImageUpload from '../common/ImageUpload';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // import styles

// Custom styling for ReactQuill with reduced sizes
const quillCustomStyles = `
  /* Reduce toolbar button sizes */
  .ql-toolbar.ql-snow button {
    width: 24px !important;
    height: 24px !important;
    padding: 2px !important;
  }
  
  .ql-toolbar.ql-snow .ql-stroke {
    stroke-width: 1.5 !important;
  }
  
  .ql-toolbar.ql-snow .ql-fill {
    fill-rule: evenodd !important;
  }
  
  /* Reduce toolbar height */
  .ql-toolbar.ql-snow {
    padding: 6px 8px !important;
    font-size: 12px !important;
  }
  
  /* Reduce editor font size */
  .ql-editor {
    font-size: 0.85rem !important;
    line-height: 1.4 !important;
    padding: 8px 12px !important;
  }
  
  .ql-editor.ql-blank::before {
    font-size: 0.85rem !important;
  }
  
  /* Reduce dropdown sizes */
  .ql-toolbar.ql-snow .ql-picker {
    font-size: 12px !important;
  }
  
  .ql-toolbar.ql-snow .ql-picker-label {
    padding: 2px 4px !important;
  }
  
  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    .ql-toolbar.ql-snow button {
      width: 20px !important;
      height: 20px !important;
      padding: 1px !important;
    }
    
    .ql-toolbar.ql-snow {
      padding: 4px 6px !important;
      font-size: 11px !important;
    }
    
    .ql-editor {
      font-size: 0.8rem !important;
      padding: 6px 8px !important;
    }
    
    .ql-editor.ql-blank::before {
      font-size: 0.8rem !important;
    }
  }
  
  /* Dark theme adjustments */
  .dark-theme .ql-toolbar.ql-snow {
    border-color: rgba(255, 255, 255, 0.12) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
  }
  
  .dark-theme .ql-toolbar.ql-snow .ql-stroke {
    stroke: rgba(255, 255, 255, 0.7) !important;
  }
  
  .dark-theme .ql-toolbar.ql-snow .ql-fill {
    fill: rgba(255, 255, 255, 0.7) !important;
  }
  
  .dark-theme .ql-container.ql-snow {
    border-color: rgba(255, 255, 255, 0.12) !important;
    background-color: rgba(255, 255, 255, 0.05) !important;
  }
  
  .dark-theme .ql-editor {
    color: rgba(255, 255, 255, 0.87) !important;
  }
  
  .dark-theme .ql-editor.ql-blank::before {
    color: rgba(255, 255, 255, 0.5) !important;
  }
  
  .dark-theme .ql-toolbar.ql-snow button:hover,
  .dark-theme .ql-toolbar.ql-snow button.ql-active {
    background-color: rgba(255, 255, 255, 0.1) !important;
  }
`;

// Inject the CSS styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.type = 'text/css';
  styleElement.innerHTML = quillCustomStyles;
  document.head.appendChild(styleElement);
}

// Helper to convert form data from camelCase to snake_case for the API
const toSnakeCase = (obj) => {
    if (obj === null || obj === undefined) {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map(v => toSnakeCase(v));
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            // Special handling for achievement fields
            if (key === 'pdfUrl') {
                acc['pdf_url'] = obj[key];
            } else if (key === 'videoUrl') {
                acc['video_url'] = obj[key];
            } else if (key === 'youtubeUrl') {
                acc['youtube_url'] = obj[key];
            } else if (key === 'imageUrl') {
                acc['image_url'] = obj[key];
            } else if (key === 'certificateLink') {
                acc['certificate_link'] = obj[key];
            } else {
                const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
                acc[snakeKey] = toSnakeCase(obj[key]);
            }
            return acc;
        }, {});
    }
    return obj;
};

// Helper to convert API data from snake_case to camelCase for the form
const toCamelCase = (obj) => {
     if (Array.isArray(obj)) {
        return obj.map(v => toCamelCase(v));
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            let camelKey = key.replace(/([-_][a-z])/g, group => group.toUpperCase().replace('-', '').replace('_', ''));
            
            // Special handling for specific fields
            if (key === 'product_images') {
                camelKey = 'productImages';
            } else if (key === 'social_profiles') {
                camelKey = 'socialProfiles';
            } else if (key === 'company_name') {
                camelKey = 'companyName';
            } else if (key === 'profile_picture_url') {
                camelKey = 'profilePictureUrl';
            } else if (key === 'company_logo_url') {
                camelKey = 'companyLogoUrl';
            } else if (key === 'cover_photo_url') {
                camelKey = 'coverPhotoUrl';
            } else if (key === 'is_published') {
                camelKey = 'isPublished';
            } else if (key === 'achievements') {
                camelKey = 'achievements';
            } else if (key === 'batches') {
                camelKey = 'batches';
            }
            
            // Handle nested achievement fields
            if (key === 'image_url') {
                camelKey = 'imageUrl';
            } else if (key === 'certificate_link') {
                camelKey = 'certificateLink';
            } else if (key === 'pdf_url') {
                camelKey = 'pdfUrl';
            } else if (key === 'video_url') {
                camelKey = 'videoUrl';
            } else if (key === 'youtube_url') {
                camelKey = 'youtubeUrl';
            }
            
            acc[camelKey] = toCamelCase(obj[key]);
            return acc;
        }, {});
    }
    return obj;
};

const DynamicContentArray = ({ fields, setFields }) => {
    const muiTheme = useMuiTheme();
    
    const handleAddField = () => {
        setFields([...fields, { id: null, fieldName: '', fieldValue: '' }]);
    };
    const handleRemoveField = (index) => {
        setFields(fields.filter((_, i) => i !== index));
    };
    const handleChange = (index, name, value) => {
        const newFields = [...fields];
        newFields[index][name] = value;
        setFields(newFields);
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 600 }}>
                Dynamic Content ({fields ? fields.length : 0})
            </Typography>
            <Stack spacing={1.5}>
                {fields.map((field, index) => (
                    <Paper 
                        key={index} 
                        sx={{ 
                            p: 1.5, 
                            border: `1px solid ${muiTheme.palette.divider}`, 
                            borderRadius: 2,
                            bgcolor: 'background.paper'
                        }}
                    >
                        <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    size="small"
                                    label="Field Title" 
                                    name="fieldName" 
                                    value={field.fieldName} 
                                    onChange={(e) => handleChange(index, 'fieldName', e.target.value)} 
                                    sx={{
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.8rem'
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box 
                                    className={muiTheme.palette.mode === 'dark' ? 'dark-theme' : ''}
                                    sx={{
                                        '& .ql-toolbar.ql-snow': {
                                            borderColor: muiTheme.palette.divider,
                                            bgcolor: 'background.paper',
                                            minHeight: '32px !important',
                                            padding: '4px 6px !important',
                                            fontSize: '11px !important'
                                        },
                                        '& .ql-container.ql-snow': {
                                            borderColor: muiTheme.palette.divider,
                                            bgcolor: 'background.paper',
                                            color: muiTheme.palette.text.primary,
                                            minHeight: '100px !important'
                                        },
                                        '& .ql-editor': {
                                            color: muiTheme.palette.text.primary,
                                            minHeight: '80px !important',
                                            fontSize: '0.8rem !important',
                                            lineHeight: '1.3 !important',
                                            padding: '6px 8px !important'
                                        },
                                        '& .ql-toolbar.ql-snow button': {
                                            width: '20px !important',
                                            height: '20px !important',
                                            padding: '1px !important'
                                        },
                                        '& .ql-toolbar.ql-snow .ql-picker': {
                                            fontSize: '11px !important'
                                        },
                                        '& .ql-toolbar.ql-snow .ql-picker-label': {
                                            padding: '1px 3px !important'
                                        }
                                    }}
                                >
                                    <ReactQuill 
                                        theme="snow"
                                        value={field.fieldValue}
                                        onChange={(content) => handleChange(index, 'fieldValue', content)}
                                        placeholder="Field Content"
                                        modules={{
                                            toolbar: [
                                                ['bold', 'italic', 'underline'],
                                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                ['link'],
                                                ['clean']
                                            ]
                                        }}
                                        formats={[
                                            'bold', 'italic', 'underline',
                                            'list', 'bullet',
                                            'link'
                                        ]}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                    <IconButton onClick={() => handleRemoveField(index)}>
                                        <RemoveCircleOutline color="error" />
                                    </IconButton>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                ))}
                <Button 
                    startIcon={<AddCircleOutline />} 
                    onClick={handleAddField} 
                    size="small"
                    sx={{
                        alignSelf: 'flex-start',
                        fontSize: '0.8rem',
                        py: 0.5,
                        px: 1.5
                    }}
                >
                    Add Content Field
                </Button>
            </Stack>
        </Box>
    );
};

// Enhanced Contact Field Array with WhatsApp and Phone Validation
const ContactFieldArray = ({ fields, setFields }) => {
  const handleAddField = () => {
    setFields([...fields, { id: null, type: 'email', value: '', country: 'SG' }]);
  };
  
  const handleRemoveField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };
  
  const handleTypeChange = (index, newType) => {
    const newFields = [...fields];
    newFields[index] = {
      ...newFields[index],
      type: newType,
      value: '', // Reset value when type changes
      country: newType === 'phone' || newType === 'whatsapp' ? 'SG' : undefined
    };
    setFields(newFields);
  };
  
  const handleValueChange = (index, newValue) => {
    const newFields = [...fields];
    const field = newFields[index];
    
    // Phone number formatting and validation
    if (field.type === 'phone' || field.type === 'whatsapp') {
      // Remove all non-digits
      const digitsOnly = newValue.replace(/\D/g, '');
      
      // Country-specific validation
      let formattedValue = digitsOnly;
      if (field.country === 'SG') {
        // Singapore: 8 digits, starts with 6, 8, or 9
        if (digitsOnly.length <= 8) {
          formattedValue = digitsOnly;
        }
      } else if (field.country === 'IN') {
        // India: 10 digits, starts with 6, 7, 8, or 9
        if (digitsOnly.length <= 10) {
          formattedValue = digitsOnly;
        }
      }
      
      newFields[index].value = formattedValue;
    } else {
      newFields[index].value = newValue;
    }
    
    setFields(newFields);
  };
  
  const handleCountryChange = (index, newCountry) => {
    const newFields = [...fields];
    newFields[index].country = newCountry;
    newFields[index].value = ''; // Reset value when country changes
    setFields(newFields);
  };
  
  const getPhonePrefix = (country) => {
    return country === 'SG' ? '+65' : '+91';
  };
  
  const validatePhoneNumber = (value, country) => {
    if (!value) return true;
    
    if (country === 'SG') {
      // Singapore: 8 digits, starts with 6, 8, or 9
      return /^[689]\d{7}$/.test(value);
    } else if (country === 'IN') {
      // India: 10 digits, starts with 6, 7, 8, or 9
      return /^[6-9]\d{9}$/.test(value);
    }
    return false;
  };
  
  const getFieldIcon = (type) => {
    switch (type) {
      case 'email': return <Email sx={{ color: '#1976d2' }} />;
      case 'phone': return <Phone sx={{ color: '#4caf50' }} />;
      case 'whatsapp': return <WhatsApp sx={{ color: '#25d366' }} />;
      default: return <Email sx={{ color: '#1976d2' }} />;
    }
  };

  return (
    <Box>
      <FormLabel sx={{ mb: 2, display: 'block', fontWeight: 'medium', fontSize: '1.1rem' }}>
        Contact Information
      </FormLabel>
      <Stack spacing={3}>
        {fields.map((field, index) => (
          <Paper key={index} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  label="Type"
                  value={field.type}
                  onChange={(e) => handleTypeChange(index, e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="email">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email sx={{ color: '#1976d2', fontSize: 20 }} />
                      Email
                    </Box>
                  </MenuItem>
                  <MenuItem value="phone">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone sx={{ color: '#4caf50', fontSize: 20 }} />
                      Phone
                    </Box>
                  </MenuItem>
                  <MenuItem value="whatsapp">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsApp sx={{ color: '#25d366', fontSize: 20 }} />
                      WhatsApp
                    </Box>
                  </MenuItem>
                </TextField>
              </Grid>
              
              {(field.type === 'phone' || field.type === 'whatsapp') && (
                <Grid item xs={12} sm={2}>
                  <TextField
                    select
                    label="Country"
                    value={field.country || 'SG'}
                    onChange={(e) => handleCountryChange(index, e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="SG">🇸🇬 Singapore</MenuItem>
                    <MenuItem value="IN">🇮🇳 India</MenuItem>
                  </TextField>
                </Grid>
              )}
              
              <Grid item xs={12} sm={field.type === 'email' ? 8 : 6}>
                <TextField
                  label={
                    field.type === 'email' 
                      ? 'Email Address' 
                      : field.type === 'whatsapp' 
                        ? 'WhatsApp Number' 
                        : 'Phone Number'
                  }
                  value={field.value}
                  onChange={(e) => handleValueChange(index, e.target.value)}
                  fullWidth
                  size="small"
                  type={field.type === 'email' ? 'email' : 'tel'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {getFieldIcon(field.type)}
                        {(field.type === 'phone' || field.type === 'whatsapp') && (
                          <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                            {getPhonePrefix(field.country)}
                          </Typography>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  error={
                    (field.type === 'phone' || field.type === 'whatsapp') && 
                    field.value && 
                    !validatePhoneNumber(field.value, field.country)
                  }
                  helperText={
                    (field.type === 'phone' || field.type === 'whatsapp') && 
                    field.value && 
                    !validatePhoneNumber(field.value, field.country)
                      ? field.country === 'SG' 
                        ? 'Enter 8 digits starting with 6, 8, or 9' 
                        : 'Enter 10 digits starting with 6, 7, 8, or 9'
                      : ''
                  }
                  placeholder={
                    field.type === 'email' 
                      ? 'example@email.com'
                      : field.country === 'SG' 
                        ? '81234567' 
                        : '9876543210'
                  }
                />
              </Grid>
              
              <Grid item xs={12} sm={1} display="flex" justifyContent="center">
                <IconButton 
                  onClick={() => handleRemoveField(index)}
                  color="error"
                  sx={{ 
                    bgcolor: 'error.light',
                    '&:hover': { 
                      bgcolor: 'error.main',
                      color: 'white',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <RemoveCircleOutline />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        ))}
        
        {fields.length === 0 && (
          <Paper 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              border: '2px dashed #e0e0e0',
              borderRadius: 2,
              bgcolor: 'grey.50'
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No contact information added yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add your email, phone, or WhatsApp to let people contact you
            </Typography>
          </Paper>
        )}
        
        <Button 
          startIcon={<AddCircleOutline />} 
          onClick={handleAddField}
          variant="outlined"
          sx={{ alignSelf: 'flex-start', px: 3, py: 1.5 }}
        >
          Add Contact
        </Button>
      </Stack>
    </Box>
  );
};

// Achievement Field Array - handles image upload, caption, and certificate link
const AchievementFieldArray = ({ fields, setFields, cardSlug }) => {
  const [fieldErrors, setFieldErrors] = useState({}); // Add error state

  // Helper function to get error message
  const getErrorMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.msg) return error.msg;
    if (Array.isArray(error)) return error.map(e => e.msg || e.toString()).join(', ');
    return error.toString();
  };

  const handleAddField = () => {
    setFields([...fields, { 
      id: null, 
      imageUrl: null, 
      caption: '', 
      certificateLink: '',
      pdfUrl: null,
      videoUrl: null,
      youtubeUrl: ''
    }]);
    setFieldErrors({}); // Clear errors when adding new field
  };
  
  const handleRemoveField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
    // Remove errors for the deleted field
    const newErrors = { ...fieldErrors };
    delete newErrors[index];
    setFieldErrors(newErrors);
  };
  
  const handleChange = (index, fieldName, value) => {
    const newFields = [...fields];
    newFields[index][fieldName] = value;
    setFields(newFields);
    
    // Clear error for this field when user makes a change
    if (fieldErrors[index]?.[fieldName]) {
      const newErrors = { ...fieldErrors };
      delete newErrors[index][fieldName];
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index];
      }
      setFieldErrors(newErrors);
    }
  };
  
  const handleImageChange = (index, file) => {
    const newFields = [...fields];
    newFields[index].imageUrl = file;
    setFields(newFields);
    
    // Clear image error if exists
    if (fieldErrors[index]?.imageUrl) {
      const newErrors = { ...fieldErrors };
      delete newErrors[index].imageUrl;
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index];
      }
      setFieldErrors(newErrors);
    }
  };

  const handlePdfChange = (index, file) => {
    const newFields = [...fields];
    newFields[index].pdfUrl = file;
    setFields(newFields);
    
    // Clear PDF error if exists
    if (fieldErrors[index]?.pdfUrl) {
      const newErrors = { ...fieldErrors };
      delete newErrors[index].pdfUrl;
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index];
      }
      setFieldErrors(newErrors);
    }
  };

  const handleVideoChange = (index, file) => {
    const newFields = [...fields];
    newFields[index].videoUrl = file;
    setFields(newFields);
    
    // Clear video error if exists
    if (fieldErrors[index]?.videoUrl) {
      const newErrors = { ...fieldErrors };
      delete newErrors[index].videoUrl;
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index];
      }
      setFieldErrors(newErrors);
    }
  };

  const handleRemoveFile = (index, fieldName) => {
    const newFields = [...fields];
    newFields[index][fieldName] = null;
    setFields(newFields);
    
    // Clear error for removed file if exists
    if (fieldErrors[index]?.[fieldName]) {
      const newErrors = { ...fieldErrors };
      delete newErrors[index][fieldName];
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index];
      }
      setFieldErrors(newErrors);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Star sx={{ mr: 1 }} />
        Achievements ({fields ? fields.length : 0})
      </Typography>
      <Stack spacing={2}>
        {fields && fields.map((field, index) => (
          <Paper 
            key={field.id || index} 
            sx={{ 
              p: 3, 
              border: (theme) => `1px solid ${theme.palette.divider}`, 
              borderRadius: 2,
              bgcolor: 'background.paper'
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <ImageUpload
                  label={`Achievement ${index + 1}`}
                  value={field.imageUrl}
                  onChange={(file) => handleImageChange(index, file)}
                  uploading={false}
                  error={fieldErrors[index]?.imageUrl ? getErrorMessage(fieldErrors[index].imageUrl) : ''}
                />
                {/* PDF Upload */}
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    error={Boolean(fieldErrors[index]?.pdfUrl)}
                  >
                    {field.pdfUrl ? `PDF: ${field.pdfUrl.name || field.pdfUrl}` : 'Upload PDF/Document'}
                    <input
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={e => {
                        if (e.target.files && e.target.files[0]) handlePdfChange(index, e.target.files[0]);
                      }}
                    />
                  </Button>
                  {fieldErrors[index]?.pdfUrl && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                      {getErrorMessage(fieldErrors[index].pdfUrl)}
                    </Typography>
                  )}
                  {field.pdfUrl && (
                    <Button color="error" size="small" onClick={() => handleRemoveFile(index, 'pdfUrl')} sx={{ mt: 1 }}>
                      Remove PDF
                    </Button>
                  )}
                </Box>
                {/* Video Upload */}
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    error={Boolean(fieldErrors[index]?.videoUrl)}
                  >
                    {field.videoUrl ? `Video: ${field.videoUrl.name || field.videoUrl}` : 'Upload Video'}
                    <input
                      type="file"
                      accept="video/*"
                      hidden
                      onChange={e => {
                        if (e.target.files && e.target.files[0]) handleVideoChange(index, e.target.files[0]);
                      }}
                    />
                  </Button>
                  {fieldErrors[index]?.videoUrl && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                      {getErrorMessage(fieldErrors[index].videoUrl)}
                    </Typography>
                  )}
                  {field.videoUrl && (
                    <Button color="error" size="small" onClick={() => handleRemoveFile(index, 'videoUrl')} sx={{ mt: 1 }}>
                      Remove Video
                    </Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  {/* YouTube Link */}
                  <TextField
                    label="YouTube Link (Optional)"
                    value={field.youtubeUrl || ''}
                    onChange={e => handleChange(index, 'youtubeUrl', e.target.value)}
                    fullWidth
                    placeholder="https://youtube.com/watch?v=..."
                    type="url"
                    error={Boolean(fieldErrors[index]?.youtubeUrl)}
                    helperText={fieldErrors[index]?.youtubeUrl ? getErrorMessage(fieldErrors[index].youtubeUrl) : ''}
                  />
                  <TextField
                    label="Achievement Caption"
                    value={field.caption || ''}
                    onChange={(e) => handleChange(index, 'caption', e.target.value)}
                    fullWidth
                    placeholder="e.g., Best Developer Award 2024"
                    error={Boolean(fieldErrors[index]?.caption)}
                    helperText={fieldErrors[index]?.caption ? getErrorMessage(fieldErrors[index].caption) : ''}
                  />
                  <TextField
                    label="Certificate Link (Optional)"
                    value={field.certificateLink || ''}
                    onChange={(e) => handleChange(index, 'certificateLink', e.target.value)}
                    fullWidth
                    placeholder="https://example.com/certificate"
                    type="url"
                    error={Boolean(fieldErrors[index]?.certificateLink)}
                    helperText={fieldErrors[index]?.certificateLink ? getErrorMessage(fieldErrors[index].certificateLink) : ''}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton 
                      onClick={() => handleRemoveField(index)}
                      color="error"
                      sx={{ 
                        bgcolor: 'error.light',
                        '&:hover': { 
                          bgcolor: 'error.main',
                          color: 'white',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button 
          startIcon={<AddCircleOutline />} 
          onClick={handleAddField} 
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Achievement
        </Button>
      </Stack>
    </Box>
  );
};

const BatchFieldArray = ({ fields, setFields, cardSlug }) => {
  const handleAddField = () => {
    setFields([...fields, { 
      id: null, 
      imageUrl: null, 
      caption: '' 
    }]);
  };
  
  const handleRemoveField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };
  
  const handleChange = (index, fieldName, value) => {
    const newFields = [...fields];
    newFields[index][fieldName] = value;
    setFields(newFields);
  };
  
  const handleImageChange = (index, file) => {
    const newFields = [...fields];
    newFields[index].imageUrl = file;
    setFields(newFields);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Star sx={{ mr: 1 }} />
        Batches ({fields ? fields.length : 0})
      </Typography>
      <Stack spacing={2}>
        {fields && fields.map((field, index) => (
          <Paper 
            key={field.id || index} 
            sx={{ 
              p: 3, 
              border: (theme) => `1px solid ${theme.palette.divider}`, 
              borderRadius: 2,
              bgcolor: 'background.paper'
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <ImageUpload
                  label={`Batch ${index + 1}`}
                  value={field.imageUrl}
                  onChange={(file) => handleImageChange(index, file)}
                  uploading={false}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  <TextField
                    label="Batch Caption"
                    value={field.caption || ''}
                    onChange={(e) => handleChange(index, 'caption', e.target.value)}
                    fullWidth
                    placeholder="e.g., 2024 Spring Batch"
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton 
                      onClick={() => handleRemoveField(index)}
                      color="error"
                      sx={{ 
                        bgcolor: 'error.light',
                        '&:hover': { 
                          bgcolor: 'error.main',
                          color: 'white',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button 
          startIcon={<AddCircleOutline />} 
          onClick={handleAddField} 
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Batch
        </Button>
      </Stack>
    </Box>
  );
};

const DynamicFieldArray = ({ label, fields, setFields, placeholder, type, options }) => {
  const handleAddField = () => {
    setFields([...fields, { id: null, ...placeholder }]);
  };
  const handleRemoveField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };
  const handleChange = (index, event) => {
    const newFields = [...fields];
    newFields[index][event.target.name] = event.target.value;
    setFields(newFields);
  };

  return (
    <Box>
      <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 'medium' }}>{label}</FormLabel>
      <Stack spacing={2}>
        {fields.map((field, index) => (
          <Stack direction="row" spacing={1} key={index} alignItems="center">
            {Object.keys(placeholder).map(key => (
              options && options[key] ?
              <TextField select label={key.charAt(0).toUpperCase() + key.slice(1)} name={key} value={field[key]} onChange={(e) => handleChange(index, e)} sx={{ flex: 1 }}>
                {options[key].map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </TextField> :
              <TextField label={key.charAt(0).toUpperCase() + key.slice(1)} name={key} value={field[key]} onChange={(e) => handleChange(index, e)} sx={{ flex: 1 }} />
            ))}
            <IconButton onClick={() => handleRemoveField(index)}><RemoveCircleOutline /></IconButton>
          </Stack>
        ))}
        <Button startIcon={<AddCircleOutline />} onClick={handleAddField}>Add {label.slice(0,-1)}</Button>
      </Stack>
    </Box>
  );
};

const steps = [
  { label: 'Content', icon: <TextFields /> },
  { label: 'Design', icon: <Palette /> },
  { label: 'Images', icon: <Image /> }
];

const ContentStep = ({ formData, setFormData, handleChange, cardSlug }) => {
  return (
    <Stack spacing={3}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Basic Info</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} required /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Designation" name="designation" value={formData.designation} onChange={handleChange} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Company Name" name="companyName" value={formData.companyName} onChange={handleChange} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleChange} /></Grid>
            </Grid>
        </Paper>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>Dynamic Content</Typography>
            <DynamicContentArray fields={formData.contents} setFields={(val) => setFormData({...formData, contents: val})} />
        </Paper>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
             <Typography variant="h6" gutterBottom>Contact, Websites & Social</Typography>
             <Stack spacing={3} sx={{mt: 2}}>
                <ContactFieldArray fields={formData.contacts} setFields={(val) => setFormData({...formData, contacts: val})} />
                <DynamicFieldArray label="Websites" fields={formData.websites} setFields={(val) => setFormData({...formData, websites: val})} placeholder={{label: '', url: ''}}/>
                <DynamicFieldArray label="Social Profiles" fields={formData.socialProfiles} setFields={(val) => setFormData({...formData, socialProfiles: val})} placeholder={{platform: 'LinkedIn', url: ''}} options={{ platform: ['LinkedIn', 'Twitter', 'GitHub', 'Facebook', 'Instagram', 'YouTube', 'TikTok', 'Website']}}/>
             </Stack>
        </Paper>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <AchievementFieldArray fields={formData.achievements} setFields={(val) => setFormData({...formData, achievements: val})} cardSlug={cardSlug} />
        </Paper>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <BatchFieldArray fields={formData.batches} setFields={(val) => setFormData({...formData, batches: val})} cardSlug={cardSlug} />
        </Paper>
    </Stack>
  );
};

const DesignStep = ({ formData, setFormData, handleChange }) => (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Design & Settings</Typography>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                    select
                    label="Theme"
                    name="theme"
                    value={formData.theme}
                    onChange={handleChange}
                    fullWidth
                >
                    <MenuItem value="modern">Modern</MenuItem>
                    <MenuItem value="corporate">Corporate</MenuItem>
                    <MenuItem value="minimalist">Minimalist</MenuItem>
                    <MenuItem value="startup">Startup</MenuItem>
                    <MenuItem value="default">Default</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.isPublished}
                            onChange={handleChange}
                            name="isPublished"
                        />
                    }
                    label="Publish Portfolio"
                />
            </Grid>
        </Grid>
    </Paper>
);

const ImageStep = ({ 
    formData, 
    isSaving, 
    onGalleryFileChange, 
    onGalleryCaptionChange, 
    onAddGalleryImage, 
    onRemoveGalleryImage,
    setFormData
}) => {
    const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);
    


    const handleRemoveGalleryImage = (index) => {
        setConfirmDeleteIndex(index);
    };

    const confirmRemoveGalleryImage = () => {
        if (confirmDeleteIndex !== null) {
            onRemoveGalleryImage(confirmDeleteIndex);
            setConfirmDeleteIndex(null);
        }
    };

    const cancelRemoveGalleryImage = () => {
        setConfirmDeleteIndex(null);
    };

    return (
        <Stack spacing={3}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Key Images</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <ImageUpload
                            label="Profile Picture" 
                            value={formData.profilePictureUrl} 
                            onChange={(file) => onGalleryFileChange('profilePictureUrl', file)} 
                            uploading={isSaving}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ImageUpload 
                            label="Company Logo" 
                            value={formData.companyLogoUrl} 
                            onChange={(file) => onGalleryFileChange('companyLogoUrl', file)}
                            uploading={isSaving}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ImageUpload 
                            label="Cover Photo" 
                            value={formData.coverPhotoUrl} 
                            onChange={(file) => onGalleryFileChange('coverPhotoUrl', file)}
                            uploading={isSaving}
                        />
                    </Grid>
                </Grid>
            </Paper>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Gallery Images</Typography>
                <Stack spacing={3}>
                    {(formData.productImages || []).map((img, index) => (
                        <Paper 
                            key={img.id || index} 
                            sx={{ 
                                p: 3, 
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                transition: 'box-shadow 0.2s',
                                '&:hover': {
                                    boxShadow: (theme) => theme.palette.mode === 'dark' 
                                        ? '0 4px 8px rgba(255,255,255,0.1)' 
                                        : '0 4px 8px rgba(0,0,0,0.1)'
                                }
                            }}
                        >
                            <Grid container spacing={3} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <ImageUpload 
                                        label={`Gallery Image ${index + 1}`} 
                                        value={img.url} 
                                        onChange={(file) => onGalleryFileChange(index, file)}
                                        uploading={isSaving}
                                    />
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <TextField
                                        label="Caption"
                                        value={img.caption || ''} 
                                        onChange={(e) => onGalleryCaptionChange(index, e.target.value)}
                                        fullWidth 
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        placeholder="Add a caption for this image..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={1} display="flex" justifyContent="center" alignItems="center">
                                    <IconButton 
                                        onClick={() => handleRemoveGalleryImage(index)}
                                        color="error"
                                        sx={{ 
                                            bgcolor: 'error.light',
                                            '&:hover': { 
                                                bgcolor: 'error.main',
                                                color: 'white',
                                                transform: 'scale(1.1)'
                                            },
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <RemoveCircleOutline />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                    
                    {(formData.productImages || []).length === 0 && (
                        <Paper 
                            sx={{ 
                                p: 4, 
                                textAlign: 'center', 
                                border: (theme) => `2px dashed ${theme.palette.divider}`,
                                borderRadius: 2,
                                bgcolor: 'background.default'
                            }}
                        >
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                No gallery images added yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Click the button below to add your first gallery image
                            </Typography>
                        </Paper>
                    )}
                    
                    <Box sx={{ pt: 2 }}>
                        <Button 
                            startIcon={<AddCircleOutline />} 
                            onClick={onAddGalleryImage} 
                            variant="contained"
                            size="large"
                            sx={{ 
                                px: 4,
                                py: 1.5,
                                borderRadius: 2
                            }}
                        >
                            Add Gallery Image
                        </Button>
                    </Box>
                </Stack>

                {/* Confirmation Dialog for Gallery Image Removal */}
                <Dialog open={confirmDeleteIndex !== null} onClose={cancelRemoveGalleryImage}>
                    <DialogTitle>Remove Gallery Image</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to remove this gallery image? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={cancelRemoveGalleryImage}>Cancel</Button>
                        <Button onClick={confirmRemoveGalleryImage} color="error" variant="contained">
                            Remove
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Stack>
    );
};

const QRCodeStep = ({ card }) => {
    if (!card || !card.slug) return <Typography>Save the card to generate a QR code.</Typography>;
    
    const qrCodeValue = generatePublicUrl(card.slug);

    const downloadQRCode = () => {
        const svg = document.getElementById("QRCode");
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();
        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const pngFile = canvas.toDataURL("image/png");
            const downloadLink = document.createElement("a");
            downloadLink.download = `${card.slug}-qrcode.png`;
            downloadLink.href = pngFile;
            downloadLink.click();
        };
        img.src = "data:image/svg+xml;base64," + btoa(svgData);
    };

    return (
        <Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <Typography variant="h5" component="h2">Share Your Card</Typography>
            <QRCodeSVG 
                id="QRCode" 
                value={qrCodeValue} 
                size={256} 
                level="H"
                includeMargin={true}
                fgColor="#000000"
                bgColor="#ffffff"
            />
            <Button onClick={downloadQRCode} variant="contained">Download QR Code</Button>
            <TextField fullWidth InputProps={{readOnly: true}} value={qrCodeValue} label="Your Public URL" />
        </Paper>
    );
};

const DUMMY_CARD_DATA = {
    name: 'Your Name',
    designation: 'Your Designation',
    companyName: 'Your Company',
    profilePictureUrl: 'https://i.pravatar.cc/300',
    companyLogoUrl: 'https://logo.clearbit.com/google.com',
    coverPhotoUrl: 'https://source.unsplash.com/1600x900/?technology,abstract',
    contents: [
        { fieldName: 'About Me', fieldValue: 'Tell us about yourself. What makes you stand out?', isBold: false, isItalic: false },
        { fieldName: 'My Services', fieldValue: 'List the services you offer.', isBold: false, isItalic: false }
    ],
    address: 'Your address',
    contacts: [],
    websites: [],
    socialProfiles: [],
    productImages: [],
    theme: 'modern',
    isPublished: false
};

const CardForm = ({ onSaveSuccess }) => {
    const { slug } = useParams();
    const { setPageTitle } = usePage();
    const { user } = useAuth();
    const { isOpen } = useSidebar();
    const { themeName } = useTheme();
    const muiTheme = useMuiTheme();
    const navigate = useNavigate();
    const isEditMode = Boolean(slug);
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(isEditMode);
    const [formData, setFormData] = useState({
        name: '',
        designation: '',
        companyName: '',
        address: '',
        theme: 'modern',
        isPublished: true,
        profilePictureUrl: null,
        companyLogoUrl: null,
        coverPhotoUrl: null,
        contacts: [],
        websites: [],
        socialProfiles: [],
        productImages: [],
        contents: [],
        achievements: [],
        batches: []
    });

    const [initialData, setInitialData] = useState(null);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [savedModalOpen, setSavedModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

    useEffect(() => {
        setPageTitle(slug ? 'Edit Card' : 'Create Card');
        const loadCard = async () => {
            if (!isEditMode) {
                setLoading(false);
                return;
            }
            
            setLoading(true);
            try {
                const data = await fetchCardDetails(slug);
                // Convert snake_case to camelCase and construct full URLs for images
                const processedData = {
                    ...toCamelCase(data),
                    profilePictureUrl: data.profile_picture_url ? getFullUrl(data.profile_picture_url) : null,
                    companyLogoUrl: data.company_logo_url ? getFullUrl(data.company_logo_url) : null,
                    coverPhotoUrl: data.cover_photo_url ? getFullUrl(data.cover_photo_url) : null,
                    productImages: data.product_images?.map(img => ({
                        ...img,
                        url: getFullUrl(img.url)
                    })) || [],
                    achievements: data.achievements?.map(achievement => ({
                        ...achievement,
                        imageUrl: getFullUrl(achievement.image_url),
                        pdfUrl: getFullUrl(achievement.pdf_url),
                        videoUrl: getFullUrl(achievement.video_url)
                    })) || [],
                    batches: data.batches?.map(batch => ({
                        ...batch,
                        imageUrl: getFullUrl(batch.image_url)
                    })) || []
                };
                setFormData(processedData);
                setInitialData(processedData);
            } catch (error) {
                console.error('Error loading card:', error);
                setError('Failed to load card details');
            } finally {
                setLoading(false);
            }
        };

        loadCard();
    }, [slug, setPageTitle, isEditMode]);

    const handleGalleryFileChange = (indexOrField, file) => {
        setFormData(prevFormData => {
            if (typeof indexOrField === 'string') {
                return { ...prevFormData, [indexOrField]: file };
            } else {
                const newImages = [...prevFormData.productImages];
                newImages[indexOrField] = { ...newImages[indexOrField], url: file };
                return { ...prevFormData, productImages: newImages };
            }
        });
    };

    const handleGalleryCaptionChange = (index, caption) => {
        const newImages = [...formData.productImages];
        newImages[index].caption = caption;
        setFormData({ ...formData, productImages: newImages });
    };

    const addGalleryImage = () => {
        setFormData({
            ...formData,
            productImages: [...(formData.productImages || []), { id: `new_${Date.now()}`, url: null, caption: '' }]
        });
    };

    const removeGalleryImage = (index) => {
        const newImages = formData.productImages.filter((_, i) => i !== index);
        setFormData({ ...formData, productImages: newImages });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData({ ...formData, [name]: newValue });

        if (name === 'name' && !slug) { // Only auto-generate slug for new cards
            setFormData(prev => ({ ...prev, slug: generateSlug(value) }));
        }
    };
    
    const _handleSubmit_old_broken = async () => {
        // This is the old, incorrect logic that will no longer be used.
        setFormData({ ...formData, loading: true });
        let currentSlug = slug;
        let cardData = { ...formData };
    
        try {
            if (!slug) {
                const newCardShell = await createCard({ name: formData.name });
                if (!newCardShell || !newCardShell.slug) {
                    throw new Error("Failed to create new card shell or get a slug.");
                }
                currentSlug = newCardShell.slug;
                cardData.id = newCardShell.id;
            }
    
            const uploadImage = async (file, fieldName) => {
                if (file instanceof File) {
                    const response = await uploadCardImage(currentSlug, fieldName, file);
                    return response.file_path;
                }
                // If it's a string (filename or URL), preserve it as is
                if (typeof file === 'string') {
                    return file;
                }
                return file;
            };
    
            const uploadNewGalleryImage = async (image) => {
                if (image.url instanceof File) {
                    const response = await uploadGalleryImage(currentSlug, image.url);
                    return { ...image, url: response.file_path };
                }
                return image;
            };
    
            if (cardData.profilePictureUrl) {
                cardData.profilePictureUrl = await uploadImage(cardData.profilePictureUrl, 'profilePicture');
            }
            if (cardData.companyLogoUrl) {
                cardData.companyLogoUrl = await uploadImage(cardData.companyLogoUrl, 'companyLogo');
            }
            if (cardData.coverPhotoUrl) {
                cardData.coverPhotoUrl = await uploadImage(cardData.coverPhotoUrl, 'coverPhoto');
            }
    
            if (cardData.productImages && cardData.productImages.length > 0) {
                 const updatedImages = await Promise.all(
                    cardData.productImages.map(img => uploadNewGalleryImage(img))
                );
                cardData.productImages = updatedImages.filter(img => img && img.url);
            }
    
            const apiPayload = toSnakeCase(cardData);
            const finalCard = await updateCard(currentSlug, apiPayload);
            
            setFormData({ ...formData, loading: false });
            if (onSaveSuccess) onSaveSuccess(finalCard);
            navigate('/admin/dashboard');
            
        } catch (error) {
            console.error("Failed to save card:", error);
            setFormData({ ...formData, loading: false });
        }
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        setError('');

        try {
            let currentSlug = slug;
    
            // Step 1: If it's a new card, create a shell to get a slug
            if (!currentSlug) {
                const newCardShell = await createCard({ name: formData.name });
                if (!newCardShell || !newCardShell.slug) {
                    throw new Error("Failed to create a new card to get a slug.");
                }
                currentSlug = newCardShell.slug;
            }
    
            // Step 2: Upload all images that need uploading
            const uploadImage = async (file, fieldName) => {
                if (file instanceof File) {
                    const response = await uploadCardImage(currentSlug, fieldName, file);
                    return response.file_path;
                }
                // If it's a string (filename or URL), preserve it as is
                if (typeof file === 'string') {
                    return file;
                }
                return file;
            };
    
            const uploadNewGalleryImage = async (image) => {
                if (image.url instanceof File) {
                    const response = await uploadGalleryImage(currentSlug, image.url);
                    return { ...image, id: (typeof image.id === 'string' && image.id.startsWith('new_')) ? null : image.id, url: response.file_path };
                }
                 if(image.id && image.id.toString().startsWith('new_')){
                    return { ...image, id: null };
                }
                return image;
            };

            const uploadAchievementImageFile = async (achievement) => {
                let processedAchievement = { ...achievement };
                
                // Upload image if it's a File
                if (achievement.imageUrl instanceof File) {
                    const response = await uploadAchievementImage(currentSlug, achievement.imageUrl);
                    processedAchievement.imageUrl = response.file_path;
                }

                // Upload PDF if it's a File
                if (achievement.pdfUrl instanceof File) {
                    const response = await uploadAchievementPdf(currentSlug, achievement.pdfUrl);
                    processedAchievement.pdfUrl = response.file_path;
                }

                // Upload video if it's a File
                if (achievement.videoUrl instanceof File) {
                    const response = await uploadAchievementVideo(currentSlug, achievement.videoUrl);
                    processedAchievement.videoUrl = response.file_path;
                }

                // Handle ID for new achievements
                if (processedAchievement.id && processedAchievement.id.toString().startsWith('new_')) {
                    processedAchievement.id = null;
                }

                return processedAchievement;
            };

            const uploadBatchImageFile = async (batch) => {
                if (batch.imageUrl instanceof File) {
                    const response = await uploadAchievementImage(currentSlug, batch.imageUrl); // Reuse achievement upload endpoint
                    return { ...batch, id: (typeof batch.id === 'string' && batch.id.startsWith('new_')) ? null : batch.id, imageUrl: response.file_path };
                }
                if(batch.id && batch.id.toString().startsWith('new_')){
                    return { ...batch, id: null };
                }
                return batch;
            };

            const profilePictureUrl = await uploadImage(formData.profilePictureUrl, 'profile_picture_url');
            const companyLogoUrl = await uploadImage(formData.companyLogoUrl, 'company_logo_url');
            const coverPhotoUrl = await uploadImage(formData.coverPhotoUrl, 'cover_photo_url');

            const processedGalleryImages = await Promise.all(
                (formData.productImages || []).map(img => uploadNewGalleryImage(img))
            );

            const processedAchievements = await Promise.all(
                (formData.achievements || []).map(achievement => uploadAchievementImageFile(achievement))
            );

            const processedBatches = await Promise.all(
                (formData.batches || []).map(batch => uploadBatchImageFile(batch))
            );

            const finalCardData = {
                ...formData,
                profilePictureUrl,
                companyLogoUrl,
                coverPhotoUrl,
                productImages: processedGalleryImages.filter(img => img && img.url && img.url !== ''),
                achievements: processedAchievements.filter(achievement => achievement && (achievement.imageUrl || achievement.caption || achievement.certificateLink)),
                batches: processedBatches.filter(batch => batch && (batch.imageUrl || batch.caption))
            };

            const apiPayload = toSnakeCase(finalCardData);
            const finalCard = await updateCard(currentSlug, apiPayload);
            
            setSnackbar({ open: true, message: 'Portfolio saved successfully!', severity: 'success' });
            setTimeout(() => {
                if (onSaveSuccess) onSaveSuccess(finalCard);
                navigate('/admin/dashboard');
            }, 1500);

        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'Failed to save portfolio.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            console.error("Failed to save card:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);

    const getStepContent = (step) => {
        console.log('getStepContent called with step:', step);
        console.log('Current formData:', formData);
        console.log('Current slug:', slug);
        
        switch (step) {
            case 0:
                return <ContentStep formData={formData} setFormData={setFormData} handleChange={handleChange} cardSlug={slug} />;
            case 1:
                return <DesignStep formData={formData} setFormData={setFormData} handleChange={handleChange} />;
            case 2:
                return <ImageStep 
                            formData={formData} 
                            isSaving={isSaving}
                            onGalleryFileChange={handleGalleryFileChange}
                            onGalleryCaptionChange={handleGalleryCaptionChange}
                            onAddGalleryImage={addGalleryImage}
                            onRemoveGalleryImage={removeGalleryImage}
                            setFormData={setFormData}
                        />;
            case 3:
                return <QRCodeStep card={formData} />;
            default:
                return 'Unknown step';
        }
    };

    if (loading) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="60vh"
                flexDirection="column"
                gap={2}
            >
                <CircularProgress />
                <Typography variant="body1" color="text.secondary">
                    Loading card details...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="60vh"
                flexDirection="column"
                gap={2}
            >
                <Typography color="error" variant="h6">
                    {error}
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={() => navigate('/admin/dashboard')}
                >
                    Back to Dashboard
                </Button>
            </Box>
        );
    }

    // Create a version of formData for the preview with snake_case keys and correct URLs
    const createPreviewData = (data) => {
        const preview = { ...toSnakeCase(data) };

        const getUrl = (value) => {
            if (!value) return null;
            if (value instanceof File) return URL.createObjectURL(value);
            if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('blob'))) return value;
            if (typeof value === 'string') return getFullUrl(value);
            return null;
        }

        preview.profile_image_url = getUrl(data.profilePictureUrl);
        preview.company_logo_url = getUrl(data.companyLogoUrl);
        preview.cover_photo_url = getUrl(data.coverPhotoUrl);

        preview.product_images = (data.productImages || []).map(img => ({
            ...img,
            url: getUrl(img.url)
        }));

        preview.achievements = (data.achievements || []).map(achievement => ({
            ...achievement,
            image_url: getUrl(achievement.imageUrl)
        }));

        preview.batches = (data.batches || []).map(batch => ({
            ...batch,
            image_url: getUrl(batch.imageUrl)
        }));

        return preview;
    };
    
    const previewData = createPreviewData(formData);

    return (
        <Box sx={{ 
            position: 'fixed',
            top: '64px', // Toolbar height
            left: { xs: 0, md: isOpen ? `${drawerWidth}px` : 0 },
            right: 0,
            bottom: 0,
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden',
            bgcolor: 'background.default',
            zIndex: 1
        }}>
            {/* Header */}
            <Box sx={{ 
                borderBottom: 1, 
                borderColor: 'divider', 
                p: { xs: 1, sm: 1.5 }, 
                bgcolor: 'background.paper',
                zIndex: 10,
                flexShrink: 0
            }}>
                <Stepper 
                    activeStep={activeStep} 
                    alternativeLabel 
                    size="small"
                    sx={{
                        '& .MuiStepLabel-label': {
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }
                    }}
                >
                    {steps.map(step => (
                        <Step key={step.label}>
                            <StepLabel StepIconComponent={() => step.icon}>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            {/* Main Content Area */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                {/* Form Content - Scrollable */}
                <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    p: { xs: 1, sm: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0
                }}>
                    <Box sx={{ flex: 1, minHeight: 0, paddingBottom: { xs: 1, sm: 2 } }}>
                        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                            {getStepContent(activeStep)}
                        </motion.div>
                    </Box>

                    {error && (
                        <Box sx={{ flexShrink: 0, mt: 1 }}>
                            <Typography color="error">
                                {error}
                            </Typography>
                        </Box>
                    )}
                </Box>

                                {/* Live Preview - Fixed */}
                <Box sx={{ 
                    width: { xs: '100%', md: '360px' },
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    borderLeft: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    minHeight: 0,
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    py: 3
                }}>
                    <Box sx={{ 
                        p: 2, 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        flexShrink: 0,
                        width: '100%'
                    }}>
                        <Typography variant="h6" align="center" sx={{ 
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            color: 'text.primary'
                        }}>
                            Live Preview
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ 
                            fontSize: '0.85rem',
                            color: 'text.secondary',
                            mt: 0.5
                        }}>
                            See how your card looks
                        </Typography>
                    </Box>
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        width: '100%',
                        py: 2,
                        px: 0,
                        bgcolor: 'grey.50',
                        overflow: 'auto',
                        minHeight: 0
                    }}>
                        {/* Phone Frame */}
                        <Box sx={{
                            width: 360,
                            height: 720,
                            bgcolor: '#1a1a1a',
                            borderRadius: '28px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            p: 1.5,
                        }}>
                            {/* Phone Notch */}
                            <Box sx={{
                                position: 'absolute',
                                top: 10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '64px',
                                height: '12px',
                                bgcolor: '#222',
                                borderRadius: '6px',
                                zIndex: 10
                            }} />
                            {/* Phone Screen */}
                            <Box sx={{
                                flex: 1,
                                width: '100%',
                                bgcolor: 'white',
                                borderRadius: '20px',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                mt: 2,
                                mb: 1,
                            }}>
                                <PublicCard card={previewData} isPreview={true} isEditMode={true} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Fixed Navigation Buttons */}
            <Box sx={{ 
                borderTop: 1, 
                borderColor: 'divider', 
                p: 1.5, 
                bgcolor: 'background.paper',
                display: 'flex', 
                justifyContent: 'space-between',
                zIndex: 10,
                flexShrink: 0,
                minHeight: 'auto'
            }}>
                <Button 
                    disabled={activeStep === 0} 
                    onClick={handleBack} 
                    startIcon={<ArrowBack />}
                    size="medium"
                >
                    Back
                </Button>

                {activeStep < steps.length - 1 ? (
                    <Button 
                        variant="contained" 
                        onClick={handleNext} 
                        endIcon={<ArrowForward />}
                        size="medium"
                    >
                        Next
                    </Button>
                ) : (
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleSubmit} 
                        startIcon={<Save />} 
                        disabled={isSaving}
                        size="medium"
                    >
                        {isSaving ? <CircularProgress size={20} /> : 'Save Card'}
                    </Button>
                )}
            </Box>

            {/* Mobile Preview Toggle Button */}
            <Box sx={{ 
                display: { xs: 'flex', md: 'none' },
                position: 'fixed',
                bottom: 20,
                right: 20,
                zIndex: 1000
            }}>
                <Button
                    variant="contained"
                    startIcon={<Phone />}
                    onClick={() => setMobilePreviewOpen(true)}
                    sx={{
                        borderRadius: '50px',
                        px: 2,
                        py: 1,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        bgcolor: 'primary.main',
                        '&:hover': {
                            bgcolor: 'primary.dark',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                        }
                    }}
                >
                    Preview
                </Button>
            </Box>

            {/* Mobile Preview Modal for smaller screens */}
            <Dialog
                open={mobilePreviewOpen}
                fullScreen
                onClose={() => setMobilePreviewOpen(false)}
                sx={{ display: { xs: 'block', md: 'none' } }}
            >
                <DialogTitle sx={{ 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Mobile Preview</Typography>
                        <IconButton onClick={() => setMobilePreviewOpen(false)}>
                            <Cancel />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: 'grey.50' }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        p: 2
                    }}>
                        <Box sx={{
                            width: 280,
                            height: 560,
                            bgcolor: '#1a1a1a',
                            borderRadius: '24px',
                            padding: '4px',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Phone Notch */}
                            <Box sx={{
                                position: 'absolute',
                                top: '4px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '60px',
                                height: '12px',
                                bgcolor: '#1a1a1a',
                                borderRadius: '6px',
                                zIndex: 10
                            }} />

                            {/* Phone Screen */}
                            <Box sx={{
                                flex: 1,
                                bgcolor: 'white',
                                borderRadius: '20px',
                                overflow: 'hidden',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {/* Phone Status Bar */}
                                <Box sx={{
                                    height: '24px',
                                    bgcolor: 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1,
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    color: '#000',
                                    zIndex: 5,
                                    flexShrink: 0
                                }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '10px' }}>
                                        9:41
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
                                        <Box sx={{ width: '12px', height: '7px', border: '0.5px solid #000', borderRadius: '1px' }} />
                                        <Box sx={{ width: '12px', height: '7px', bgcolor: '#000', borderRadius: '1px' }} />
                                        <Box sx={{ width: '16px', height: '7px', border: '0.5px solid #000', borderRadius: '1px', position: 'relative' }}>
                                            <Box sx={{ width: '70%', height: '4px', bgcolor: '#00C851', borderRadius: '1px', position: 'absolute', top: '1px', left: '1px' }} />
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Card Content */}
                                <Box sx={{ 
                                    flex: 1, 
                                    overflowX: 'hidden',
                                    overflowY: 'auto',
                                    height: '100%',
                                    width: '100%',
                                    position: 'relative'
                                }}>
                                    <Box sx={{
                                        transform: 'scale(0.85)',
                                        transformOrigin: 'top center',
                                        width: '117.65%',
                                        marginLeft: '-8.825%',
                                        height: '100%',
                                        overflow: 'hidden'
                                    }}>
                                        <PublicCard card={previewData} isPreview={true} isEditMode={true} />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <Modal
                open={savedModalOpen}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={savedModalOpen}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 300,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                    }}>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                                type: 'spring',
                                stiffness: 260,
                                damping: 20,
                            }}
                        >
                            <CheckCircle color="success" sx={{ fontSize: 60 }} />
                        </motion.div>
                        <Typography variant="h6">Portfolio Saved!</Typography>
                    </Box>
                </Fade>
            </Modal>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CardForm;